import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useContentDetail } from '@/hooks/useContentQueries';
import { getContentTypeIcon } from '@/lib/content-labels';
import { VideoPlayer } from '@/features/viewers/Video';
import { cn } from '@/lib/utils';
import { combineAudioBlobs } from '@/lib/audio';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import {
  Camera,
  CircleDot,
  FileAudio,
  ImagePlus,
  Mic,
  Pause,
  Play,
  RotateCcw,
  Send,
  Square,
  Video,
  X,
} from 'lucide-react';
import type { CaptureEdgeKind } from '@vivport/backend/types';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ObservationOption } from '@/components/provenance/observation-labels';
import { CAPTURE_MODES, CaptureMode, OBSERVATION_CAPTURE_LABELS as L } from './labels';
import { ChatComposerWaveform } from '@/features/speak2me/components/ChatComposerWaveform';
import { CaptureSessionList } from './capture-session-list';
import { useMicAnalyser } from './use-mic-analyser';
import { useObservationCaptureSubmit } from './use-observation-capture-submit';
import { enqueueSerial } from '@/lib/serial-queue';
import { useCaptureFlowStore, type CaptureRound, type StagedItem } from '@/store/useCaptureFlowStore';

// A video is never analysed, so its file name IS its permanent title. Local time, and
// no colon or slash — the form has to survive a filesystem and a Content-Disposition.
const recordedVideoName = (extension: string): string => {
  const d = new Date();
  const p2 = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}.${p2(d.getMinutes())}.${p2(d.getSeconds())}`;
  return `Video ${stamp}.${extension}`;
};

const MODE_ICONS = { recording: Mic, photo: Camera, video: Video } as const;

const ACTION_BUTTON = 'h-7 rounded-md px-2.5 text-xs';
const ICON_BUTTON = 'h-7 w-7';

const NEW_OBSERVATION = 'new';

const EMPTY_STAGED: StagedItem[] = [];
const EMPTY_ROUNDS: CaptureRound[] = [];

function stagedParts(items: readonly StagedItem[]): string[] {
  const parts: string[] = [];
  const photos = items.filter((item) => item.kind === 'photo').length;
  const recordings = items.filter((item) => item.kind === 'recording').length;
  const videos = items.filter((item) => item.kind === 'video').length;
  if (photos > 0) parts.push(L.session.photoCount(photos));
  if (recordings > 0) parts.push(L.session.recordingCount(recordings));
  if (videos > 0) parts.push(L.session.videoCount(videos));
  return parts;
}

interface ObservationCaptureProps {
  /** Keys the session's staged tray, rounds, and observation group in the capture store. */
  sessionId: string;
  /** The shell the host mounts it in; defaults to the standalone dialog box. */
  className?: string;
  defaultMode?: CaptureMode;
  linkContentId?: number | null;
  /**
   * The NeoChat this tool is mounted in. Sent with every create so the capture is filed
   * against that chat; the Explore dialog omits it, and the server opens a ManualSession
   * as the fallback for a CRUD event with no origin.
   */
  originProvenanceContentId?: number;
  /** Observations in this chat. Absent/empty (the Explore dialog) means no selector. */
  observationOptions?: readonly ObservationOption[];
  /** The chosen Observation; null selects New. */
  selectedObservationGroupId?: number | null;
  onSelectObservation?: (groupId: number | null) => void;
  /** Injected by the evidence-tool frame; absent when no other tool shares the slot. */
  modelSlot?: ReactNode;
  switchSlot?: ReactNode;
  onSubmitted: (result: {
    photoIds: number[];
    recordingIds: number[];
    videoIds: number[];
    linkContentId: number | null;
    edgeKind: CaptureEdgeKind | null;
    observationGroupId: number | null;
  }) => void;
  disabled?: boolean;
}

// An uploaded file carries its own duration; a non-finite value (a stream, or metadata
// the browser cannot read) is a legitimate absent duration, not a failure to hide — the
// column is nullable and the upload proceeds either way.
function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const element = document.createElement('video');
    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      const value = element.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(value) ? value : null);
    };
    element.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    element.src = url;
  });
}

function resolveInitialMode(defaultMode?: CaptureMode): CaptureMode {
  return defaultMode === 'photo' || defaultMode === 'video' ? defaultMode : 'recording';
}

export function ObservationCapture({
  sessionId,
  className,
  defaultMode,
  linkContentId = null,
  originProvenanceContentId,
  observationOptions,
  selectedObservationGroupId = null,
  onSelectObservation,
  modelSlot,
  switchSlot,
  onSubmitted,
  disabled = false,
}: ObservationCaptureProps) {
  const [mode, setMode] = useState<CaptureMode>(() => resolveInitialMode(defaultMode));
  const [linkId, setLinkId] = useState<number | null>(linkContentId);
  const [error, setError] = useState<string | null>(null);

  const { submitStaged } = useObservationCaptureSubmit();

  // The session's observation: the first submit starts the group, every later submit
  // joins it. Independent of linkId — the business link files evidence, the
  // observation records co-capture; clearing the link never changes the grouping.
  // Read from the store rather than the render, because a queued submit resolves its
  // group when it reaches the front of the queue, not when the member pressed Send.
  const observationGroupInput = () => {
    const groupId = useCaptureFlowStore.getState().observationGroupIdByItemId[sessionId];
    return groupId != null ? { groupId } : ({ start: true } as const);
  };

  const linkDetail = useContentDetail(linkId ?? 0, { enabled: linkId !== null });

  // One tray for every mode, held in the store rather than here: the tabs choose which
  // tool adds the next item, and the member may swap to another evidence tool entirely
  // mid-session, so nothing staged may depend on this component staying mounted.
  const staged = useCaptureFlowStore(
    (state) => state.stagedBySessionId[sessionId] ?? EMPTY_STAGED
  );
  const rounds = useCaptureFlowStore((state) => state.roundsByItemId[sessionId] ?? EMPTY_ROUNDS);
  const addStagedItems = useCaptureFlowStore((state) => state.addStagedItems);
  const replaceStagedItem = useCaptureFlowStore((state) => state.replaceStagedItem);
  const removeStagedItem = useCaptureFlowStore((state) => state.removeStagedItem);
  const beginSubmitting = useCaptureFlowStore((state) => state.beginSubmitting);
  const retireInFlight = useCaptureFlowStore((state) => state.retireInFlight);
  const returnInFlight = useCaptureFlowStore((state) => state.returnInFlight);
  const recordSessionObservationGroup = useCaptureFlowStore(
    (state) => state.recordSessionObservationGroup
  );
  const stagedRef = useRef<StagedItem[]>(EMPTY_STAGED);
  stagedRef.current = staged;
  // Punch-in target: onstop reads the ref (it is not re-created per render), the row
  // renders from the state twin.
  const punchTargetRef = useRef<{ id: string; resumeTime: number } | null>(null);
  const [punchingId, setPunchingId] = useState<string | null>(null);
  const [splicingId, setSplicingId] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const analyser = useMicAnalyser(micStream);
  const chunksRef = useRef<Blob[]>([]);
  const audioRefs = useRef(new Map<string, HTMLAudioElement>());
  const audioUploadInputRef = useRef<HTMLInputElement>(null);
  const unmountedRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const photoUploadInputRef = useRef<HTMLInputElement>(null);
  const videoUploadInputRef = useRef<HTMLInputElement>(null);
  const [isVideoRecording, setIsVideoRecording] = useState(false);
  // MediaRecorder's webm output reports Infinity for duration, so a recorded take's
  // length is timed here instead, pausing and resuming with the recorder.
  const videoElapsedRef = useRef<{ accumulatedMs: number; startedAt: number | null }>({
    accumulatedMs: 0,
    startedAt: null,
  });
  const [videoElapsedSeconds, setVideoElapsedSeconds] = useState(0);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Reset on every effect run, not just initialised once: StrictMode's mount/cleanup/mount
  // cycle reuses the same refs, so a cleanup-only assignment leaves this stuck true for the
  // component's whole life and onstop silently discards every take.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    if ((cameraActive || isVideoRecording) && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive, isVideoRecording]);

  useEffect(() => {
    if (!isVideoRecording) {
      setVideoElapsedSeconds(0);
      return;
    }
    const tick = () => {
      const elapsed = videoElapsedRef.current;
      const runningMs = elapsed.startedAt === null ? 0 : Date.now() - elapsed.startedAt;
      setVideoElapsedSeconds(Math.floor((elapsed.accumulatedMs + runningMs) / 1000));
    };
    tick();
    const handle = setInterval(tick, 1000);
    return () => clearInterval(handle);
  }, [isVideoRecording, isPaused]);

  const addStaged = (kind: StagedItem['kind'], files: File[]) => {
    addStagedItems(
      sessionId,
      files.map((file) => ({
        id: crypto.randomUUID(),
        kind,
        file,
        url: URL.createObjectURL(file),
      }))
    );
  };

  const addStagedVideo = (file: File, durationSeconds: number | null) => {
    addStagedItems(sessionId, [
      { id: crypto.randomUUID(), kind: 'video', file, url: URL.createObjectURL(file), durationSeconds },
    ]);
  };

  const removeStaged = (id: string) => {
    audioRefs.current.delete(id);
    removeStagedItem(sessionId, id);
  };

  // Both modes share streamRef, so leaving a mode must release its device here rather
  // than only on unmount. A running recorder is stopped, never torn down: onstop is what
  // stages the take and releases the microphone, so this must not touch the recorder ref
  // or the chunks before it has run.
  const releaseDevice = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      setIsRecording(false);
      setIsPaused(false);
      setIsVideoRecording(false);
      return;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraActive(false);
    setIsVideoRecording(false);
    setCameraError(null);
  };

  const changeMode = (next: CaptureMode) => {
    if (next === mode) return;
    releaseDevice();
    setMode(next);
  };

  const startRecording = async (punchTarget: { id: string; resumeTime: number } | null = null) => {
    if (disabled) return;
    punchTargetRef.current = punchTarget;
    setPunchingId(punchTarget?.id ?? null);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    setMicStream(stream);
    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setMicStream(null);
      const chunks = chunksRef.current;
      chunksRef.current = [];
      mediaRecorderRef.current = null;
      const punchTarget = punchTargetRef.current;
      punchTargetRef.current = null;
      if (unmountedRef.current) return;
      setPunchingId(null);
      const file = new File(chunks, `recording-${Date.now()}.webm`, {
        type: recorder.mimeType || 'audio/webm',
      });
      if (punchTarget === null) {
        addStaged('recording', [file]);
        return;
      }
      void spliceIntoStaged(punchTarget, file);
    };

    recorder.start();
    setIsRecording(true);
    setIsPaused(false);
  };

  const pauseOrResumeRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'recording') {
      recorder.pause();
      const elapsed = videoElapsedRef.current;
      if (elapsed.startedAt !== null) {
        elapsed.accumulatedMs += Date.now() - elapsed.startedAt;
        elapsed.startedAt = null;
      }
      setIsPaused(true);
      return;
    }
    if (recorder.state === 'paused') {
      recorder.resume();
      videoElapsedRef.current.startedAt = Date.now();
      setIsPaused(false);
    }
  };

  const startVideoRecording = () => {
    if (disabled) return;
    // The camera is opened first (startVideoCamera), so the stream is already live and
    // framed; recording attaches a recorder to it rather than acquiring a second one.
    const stream = streamRef.current;
    if (!stream) return;
    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];
    videoElapsedRef.current = { accumulatedMs: 0, startedAt: Date.now() };

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      const chunks = chunksRef.current;
      chunksRef.current = [];
      mediaRecorderRef.current = null;
      const elapsed = videoElapsedRef.current;
      const runningMs = elapsed.startedAt === null ? 0 : Date.now() - elapsed.startedAt;
      const durationSeconds = Math.max(0, Math.round((elapsed.accumulatedMs + runningMs) / 1000));
      videoElapsedRef.current = { accumulatedMs: 0, startedAt: null };
      setIsVideoRecording(false);
      setCameraActive(false);
      if (unmountedRef.current) return;
      // The codecs parameter is stripped so the platform classifies on the base video/
      // prefix rather than falling through to the extension table.
      const rawMime = recorder.mimeType || 'video/webm';
      const baseMime = rawMime.split(';')[0].trim();
      const extension = baseMime.split('/')[1]?.replace(/^x-/, '') || 'webm';
      const file = new File(chunks, recordedVideoName(extension), { type: baseMime });
      addStagedVideo(file, durationSeconds);
    };

    recorder.start();
    setIsRecording(true);
    setIsPaused(false);
    setIsVideoRecording(true);
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
    setIsRecording(false);
    setIsPaused(false);
  };

  // Destructive punch-in, as it worked before: the take is trimmed at the playhead and
  // the new audio replaces everything after it. combineAudioBlobs re-encodes to WAV, so
  // only a spliced take pays that size; untouched takes stay as the recorder made them.
  const spliceIntoStaged = async (
    target: { id: string; resumeTime: number },
    take: File
  ): Promise<void> => {
    const existing = stagedRef.current.find((item) => item.id === target.id);
    if (!existing) {
      addStaged('recording', [take]);
      return;
    }
    setSplicingId(target.id);
    try {
      const combined = await combineAudioBlobs(existing.file, target.resumeTime, take);
      if (unmountedRef.current) return;
      const file = new File([combined], `recording-${Date.now()}.wav`, { type: combined.type });
      const url = URL.createObjectURL(file);
      audioRefs.current.delete(target.id);
      replaceStagedItem(sessionId, target.id, { ...existing, file, url });
    } catch (err) {
      setError(getAxiosErrorDisplayMessage(err, L.staged.spliceFailed));
    } finally {
      setSplicingId(null);
    }
  };

  const rewindStaged = (id: string) => {
    const audio = audioRefs.current.get(id);
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 5);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  const startCamera = async () => {
    if (disabled) return;
    setCameraError(null);
    stopCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setCameraActive(true);
    } catch {
      setCameraError(L.photo.cameraError);
    }
  };

  const startVideoCamera = async () => {
    if (disabled) return;
    setCameraError(null);
    stopCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: true,
      });
      streamRef.current = stream;
      setCameraActive(true);
    } catch {
      setCameraError(L.video.cameraError);
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    );
    if (!blob) return;
    addStaged('photo', [new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' })]);
  };

  // Submitting is not finishing, and it never makes the member wait: the tray empties at
  // once, Send is immediately usable again, and the upload runs on the session's queue.
  // Only what this submit actually sent may be retired — the member keeps capturing while
  // it runs, so anything staged after the snapshot must survive untouched.
  const submitTray = () => {
    if (disabled) return;
    // Read the tray from the store, not from this render: a second call before React has
    // re-rendered would otherwise still see the items it just handed to the queue and
    // enqueue them a second time, uploading the member's evidence twice.
    const submitted = useCaptureFlowStore.getState().stagedBySessionId[sessionId] ?? EMPTY_STAGED;
    if (submitted.length === 0) return;
    setError(null);
    const submittedIds = submitted.map((item) => item.id);
    submittedIds.forEach((id) => audioRefs.current.delete(id));
    beginSubmitting(sessionId, submittedIds);

    enqueueSerial(sessionId, async () => {
      // Resolved here rather than at enqueue: the queue is serial, so an earlier submit
      // has already recorded the session's group by now and `{ start: true }` cannot be
      // sent twice. The cost is that choosing New while a submit is still in flight joins
      // that observation instead of starting a second one.
      const result = await submitStaged(
        submitted,
        linkId,
        observationGroupInput(),
        originProvenanceContentId
      );
      // Recorded here, not from the host's onSubmitted: every door that mounts this tool
      // must keep one session to one observation, and a partial success that started a
      // group still has to be joined by the next submit.
      if (result.observationGroupId !== null) {
        recordSessionObservationGroup(sessionId, result.observationGroupId);
      }
      const failed = new Set(result.failedItemIds);
      retireInFlight(
        sessionId,
        submittedIds.filter((id) => !failed.has(id))
      );
      if (failed.size > 0) returnInFlight(sessionId, [...failed]);
      if (result.error !== null) {
        setError(getAxiosErrorDisplayMessage(result.error, L.staged.submitFailed));
      }
      if (result.photoIds.length > 0 || result.recordingIds.length > 0 || result.videoIds.length > 0) {
        onSubmitted({
          photoIds: result.photoIds,
          recordingIds: result.recordingIds,
          videoIds: result.videoIds,
          linkContentId: linkId,
          edgeKind: result.edgeKind,
          observationGroupId: result.observationGroupId,
        });
      }
    });
  };

  const LinkIcon = getContentTypeIcon(linkDetail.data?.contentType);
  const hasObservationChoice = (observationOptions?.length ?? 0) > 0;
  const submitTitle =
    staged.length === 0 ? L.staged.submitEmpty : L.staged.submit(stagedParts(staged));
  const stagedPhotos = staged.filter((item) => item.kind === 'photo');
  const stagedRecordings = staged.filter((item) => item.kind === 'recording');
  const stagedVideos = staged.filter((item) => item.kind === 'video');

  return (
    <div
      data-testid="observation-capture"
      className={cn('text-sm space-y-2', className ?? 'rounded border bg-muted/40 p-3')}
    >
      {!hasObservationChoice && rounds.length > 0 ? <CaptureSessionList rounds={rounds} /> : null}

      {linkId !== null ? (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">{L.linkedTo}</span>
          <LinkIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-medium">{linkDetail.data?.title ?? L.linkLoading}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={ACTION_BUTTON}
            aria-label={L.linkClear}
            onClick={() => setLinkId(null)}
            disabled={disabled}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground">
        {hasObservationChoice ? (
          <span className="inline-flex items-center gap-1">
            {L.modeHintGroupPrefix(L.modeMedia[mode])}
            <Select
              value={selectedObservationGroupId === null ? NEW_OBSERVATION : String(selectedObservationGroupId)}
              onValueChange={(next) =>
                onSelectObservation?.(next === NEW_OBSERVATION ? null : Number(next))
              }
              disabled={disabled}
            >
              <SelectTrigger
                data-testid="observation-group-select"
                aria-label={L.observationSelectLabel}
                className="h-6 w-auto gap-1 border-0 bg-transparent px-1 text-xs font-medium text-foreground shadow-none hover:bg-accent focus:ring-0 focus:ring-offset-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                {observationOptions?.map((option) => (
                  <SelectItem key={option.groupId} value={String(option.groupId)} className="text-xs">
                    {option.label}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_OBSERVATION} className="text-xs">
                  {L.observationNew}
                </SelectItem>
              </SelectContent>
            </Select>
          </span>
        ) : (
          L.modeHint(L.modeMedia[mode])
        )}
      </p>

      {mode === 'recording' ? (
        <div className="space-y-2">
          {isRecording ? (
            <div className="px-1">
              <ChatComposerWaveform analyser={analyser} paused={isPaused} />
            </div>
          ) : null}

          <div className="flex items-center gap-2 flex-wrap">
            {!isRecording ? (
              stagedRecordings.length > 0 ? null : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className={ACTION_BUTTON}
                    onClick={() => startRecording()}
                    disabled={disabled}
                  >
                    <Play className="h-3.5 w-3.5 mr-1" />
                    {L.recording.record}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={ACTION_BUTTON}
                    onClick={() => audioUploadInputRef.current?.click()}
                    disabled={disabled}
                  >
                    <FileAudio className="h-3.5 w-3.5 mr-1" />
                    {L.recording.upload}
                  </Button>
                </>
              )
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className={ACTION_BUTTON}
                  onClick={pauseOrResumeRecording}
                  disabled={disabled}
                >
                  {isPaused ? <Play className="h-3.5 w-3.5 mr-1" /> : <Pause className="h-3.5 w-3.5 mr-1" />}
                  {isPaused ? L.recording.resume : L.recording.pause}
                </Button>
                <Button variant="secondary" size="sm" className={ACTION_BUTTON} onClick={stopRecording} disabled={disabled}>
                  <Square className="h-3.5 w-3.5 mr-1" />
                  {L.recording.stop}
                </Button>
              </>
            )}
          </div>

          <input
            ref={audioUploadInputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) addStaged('recording', [file]);
              event.target.value = '';
            }}
          />

        </div>
      ) : null}

      {mode === 'photo' ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className={ACTION_BUTTON}
              onClick={() => {
                stopCamera();
                photoUploadInputRef.current?.click();
              }}
              disabled={disabled}
            >
              <ImagePlus className="h-3.5 w-3.5 mr-1" />
              {L.photo.upload}
            </Button>
            {!cameraActive ? (
              <Button variant="outline" size="sm" className={ACTION_BUTTON} onClick={startCamera} disabled={disabled}>
                <Camera className="h-3.5 w-3.5 mr-1" />
                {L.photo.take}
              </Button>
            ) : (
              <Button variant="secondary" size="sm" className={ACTION_BUTTON} onClick={stopCamera} disabled={disabled}>
                <X className="h-3.5 w-3.5 mr-1" />
                {L.photo.close}
              </Button>
            )}
          </div>

          <input
            ref={photoUploadInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              if (files.length > 0) addStaged('photo', files);
              event.target.value = '';
            }}
          />

          {cameraError ? <p className="text-xs text-destructive">{cameraError}</p> : null}

          {cameraActive ? (
            <div className="space-y-2">
              <video ref={videoRef} autoPlay playsInline muted className="w-full rounded" />
              <Button variant="outline" size="sm" className={ACTION_BUTTON} onClick={capturePhoto} disabled={disabled}>
                <CircleDot className="h-3.5 w-3.5 mr-1" />
                {L.photo.capture}
              </Button>
            </div>
          ) : null}

        </div>
      ) : null}

      {mode === 'video' ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {!cameraActive && !isVideoRecording ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className={ACTION_BUTTON}
                  onClick={() => {
                    stopCamera();
                    videoUploadInputRef.current?.click();
                  }}
                  disabled={disabled}
                >
                  <Video className="h-3.5 w-3.5 mr-1" />
                  {L.video.upload}
                </Button>
                <Button variant="outline" size="sm" className={ACTION_BUTTON} onClick={startVideoCamera} disabled={disabled}>
                  <Video className="h-3.5 w-3.5 mr-1" />
                  {L.video.record}
                </Button>
              </>
            ) : null}

            {cameraActive && !isVideoRecording ? (
              <>
                <Button variant="outline" size="sm" className={ACTION_BUTTON} onClick={startVideoRecording} disabled={disabled}>
                  <CircleDot className="h-3.5 w-3.5 mr-1" />
                  {L.video.start}
                </Button>
                <Button variant="secondary" size="sm" className={ACTION_BUTTON} onClick={stopCamera} disabled={disabled}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  {L.video.close}
                </Button>
              </>
            ) : null}

            {isVideoRecording ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className={ACTION_BUTTON}
                  onClick={pauseOrResumeRecording}
                  disabled={disabled}
                >
                  {isPaused ? <Play className="h-3.5 w-3.5 mr-1" /> : <Pause className="h-3.5 w-3.5 mr-1" />}
                  {isPaused ? L.video.resume : L.video.pause}
                </Button>
                <Button variant="secondary" size="sm" className={ACTION_BUTTON} onClick={stopRecording} disabled={disabled}>
                  <Square className="h-3.5 w-3.5 mr-1" />
                  {L.video.stop}
                </Button>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {L.video.elapsed(videoElapsedSeconds)}
                </span>
              </>
            ) : null}
          </div>

          <input
            ref={videoUploadInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              void readVideoDuration(file).then((durationSeconds) => {
                addStagedVideo(file, durationSeconds);
              });
            }}
          />

          {cameraError ? <p className="text-xs text-destructive">{cameraError}</p> : null}

          {cameraActive || isVideoRecording ? (
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded" />
          ) : null}
        </div>
      ) : null}

      {staged.length > 0 ? (
        <div className="space-y-2">
          {stagedPhotos.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {stagedPhotos.map((item) => (
                <div key={item.id} className="relative">
                  <img
                    src={item.url}
                    alt={item.file.name}
                    className="h-20 w-20 rounded object-cover"
                  />
                  <button
                    type="button"
                    aria-label={L.staged.remove(item.file.name)}
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-background border p-0.5"
                    onClick={() => removeStaged(item.id)}
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {stagedVideos.map((item) => (
            <div key={item.id} className="space-y-1">
              <VideoPlayer src={item.url} />
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={ICON_BUTTON}
                  aria-label={L.staged.remove(item.file.name)}
                  onClick={() => removeStaged(item.id)}
                  disabled={disabled}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}

          {stagedRecordings.map((item) => {
            const punching = punchingId === item.id;
            const splicing = splicingId === item.id;
            const busy = disabled || splicing;
            return (
              <div key={item.id} className="space-y-3">
                <audio
                  ref={(element) => {
                    if (element) audioRefs.current.set(item.id, element);
                    else audioRefs.current.delete(item.id);
                  }}
                  controls
                  src={item.url}
                  className="w-full"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className={ACTION_BUTTON}
                    onClick={() => rewindStaged(item.id)}
                    disabled={busy || punching}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    {L.recording.rewind}
                  </Button>
                  {punching ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className={ACTION_BUTTON}
                      onClick={stopRecording}
                      disabled={disabled}
                    >
                      <Square className="h-3.5 w-3.5 mr-1" />
                      {L.recording.stop}
                    </Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            variant="outline"
                            size="sm"
                            className={ACTION_BUTTON}
                            onClick={() =>
                              startRecording({
                                id: item.id,
                                resumeTime: audioRefs.current.get(item.id)?.currentTime ?? 0,
                              })
                            }
                            disabled={busy || isRecording}
                          >
                            <Mic className="h-3.5 w-3.5 mr-1" />
                            {L.staged.recordFromHere}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="mr-2" sideOffset={4}>
                        <p>{L.staged.punchHint}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {splicing ? (
                    <span className="text-xs text-muted-foreground">{L.staged.splicing}</span>
                  ) : null}
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn('ml-auto', ICON_BUTTON)}
                    aria-label={L.staged.remove(item.file.name)}
                    onClick={() => removeStaged(item.id)}
                    disabled={busy || punching}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}

        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-1 pt-1">
        {modelSlot}
        <div className="flex-1" />
        {switchSlot}

        {CAPTURE_MODES.map((candidate) => {
          const Icon = MODE_ICONS[candidate];
          const active = candidate === mode;
          return (
            <Tooltip key={candidate}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-7 w-7',
                      active
                        ? 'bg-foreground text-background hover:bg-foreground/90 hover:text-background'
                        : 'text-muted-foreground'
                    )}
                    aria-pressed={active}
                    onClick={() => changeMode(candidate)}
                    disabled={disabled}
                    title={L.modes[candidate]}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="mr-2" sideOffset={4}>
                <p>{L.modes[candidate]}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                type="button"
                size="icon"
                className="h-7 w-7"
                onClick={submitTray}
                disabled={disabled || staged.length === 0}
                title={submitTitle}
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="mr-2" sideOffset={4}>
            <p>{submitTitle}</p>
          </TooltipContent>
        </Tooltip>

      </div>
    </div>
  );
}
